/**
 * walletAvailable.ts — WEB-2826: ОДНА дверь «сколько кредитов свободно и
 * сколько в резерве».
 *
 * Сервер (WEB-2837, ADR-0027) отдаёт в обзоре `/billing` рядом с суммой
 * порций `balanceCents` ещё `availableCents` (свободный остаток: порции минус
 * действующие резервы идущих действий ИИ, ниже нуля не бывает) и `heldCents`
 * (сколько отложено в резерв). Каждую новую трату сервер сверяет со
 * СВОБОДНЫМ остатком — значит, и кабинет обязан и показывать, и сверять
 * именно его (урок WEB-1242: кабинет и сервер дают одно число).
 *
 * Все читатели (срез биллинга, окно генерации, окна доменов, страница
 * «Баланс», индикатор в шапке) берут свободный остаток и резерв только
 * отсюда — правило «старый ответ без полей = свободно всё, резерва нет»
 * живёт в одном месте.
 *
 * Единицы: центы и есть кредиты (1 Cr = $0.01), как у `balanceCents`.
 */

/** Числа кошелька в форме ответа `/billing` (поля резерва необязательны). */
export interface WalletFigures {
  balanceCents?: number | null;
  availableCents?: number | null;
  heldCents?: number | null;
}

function isFiniteNumber(value: unknown): value is number {
  return typeof value === 'number' && Number.isFinite(value);
}

/**
 * Свободный остаток в центах (= кредитах).
 *
 * - сервер прислал `availableCents` — берём его (ниже нуля не опускаем);
 * - старый сервер без поля — свободно столько же, сколько в порциях;
 * - баланс неизвестен вовсе — `null`: гейт на догадке срабатывать не должен.
 */
export function walletAvailableCents(
  figures: WalletFigures | null | undefined,
): number | null {
  if (!figures) return null;
  if (isFiniteNumber(figures.availableCents)) {
    return Math.max(0, figures.availableCents);
  }
  if (isFiniteNumber(figures.balanceCents)) return figures.balanceCents;
  return null;
}

/** Сколько отложено в резерв идущими действиями ИИ; нет данных — ноль. */
export function walletHeldCents(
  figures: WalletFigures | null | undefined,
): number {
  if (!figures || !isFiniteNumber(figures.heldCents)) return 0;
  return Math.max(0, figures.heldCents);
}

/**
 * Состояние стора, из которого читаются числа кошелька. Описано по форме,
 * а не импортом среза: селекторы живут здесь, а не в billingSlice.ts, —
 * десятки наборов подменяют срез целиком (`jest.mock('@/store/billingSlice')`)
 * без новых выходов, и селектор оттуда пришёл бы в компонент `undefined`.
 */
interface StateWithBilling {
  billing?:
    | (WalletFigures & { billingLoaded?: boolean; walletConfirmed?: boolean })
    | null;
}

/**
 * Кошелёк известен: загрузка завершилась И хоть раз была удачной. Упавшая
 * первая загрузка ставит `billingLoaded`, а числа там — начальный 0 (добор
 * приёмки WEB-2826). Признак `walletConfirmed` отсутствует (старая форма
 * состояния в подменах тестов) — судим по `billingLoaded`, как раньше.
 */
function walletKnown(billing: StateWithBilling['billing']): boolean {
  if (!billing?.billingLoaded) return false;
  return billing.walletConfirmed !== false;
}

/**
 * Свободный остаток кошелька из стора — с ним сервер сверяет каждую новую
 * трату. До первой загрузки — `null` (не знаем; гейт на догадке не
 * срабатывает).
 */
export function selectWalletAvailableCents(
  state: StateWithBilling,
): number | null {
  const billing = state.billing;
  if (!walletKnown(billing)) return null;
  return walletAvailableCents(billing);
}

/** Резерв идущих действий ИИ из стора (0 — резерва нет или не загружено). */
export function selectWalletHeldCents(state: StateWithBilling): number {
  const billing = state.billing;
  if (!walletKnown(billing)) return 0;
  return walletHeldCents(billing);
}
